import React, { useRef } from 'react';
import Papa from 'papaparse';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Upload } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { toast } from "@/hooks/use-toast";
import { extractHandlesFromCsvRows } from '@/lib/utils/csvParsing';

interface CampaignFormStep2Props {
  creatorUsernames: string;
  mergedHandles: string[];
  csvFileName: string | null;
  selectedCreatorLists: string[];
  createLoading: boolean;
  createError: string | null;
  onSetCreatorUsernames: (value: string) => void;
  onCsvHandlesParsed: (handles: string[], fileName: string) => void;
  onRemoveHandle: (handle: string) => void;
  onCreatorListSelect: (listId: string) => void;
  onRemoveCreatorList: (listId: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  onClose: () => void;
}

export const CampaignFormStep2: React.FC<CampaignFormStep2Props> = ({
  creatorUsernames,
  mergedHandles,
  csvFileName,
  selectedCreatorLists,
  createLoading,
  createError,
  onSetCreatorUsernames,
  onCsvHandlesParsed,
  onRemoveHandle,
  onCreatorListSelect,
  onRemoveCreatorList,
  onSubmit,
  onBack,
  onClose,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast({
        title: "Invalid file",
        description: "Please upload a CSV file.",
        variant: "destructive",
      });
      return;
    }

    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const fatalErrors = results.errors.filter(
            (err) => err.code !== 'UndetectableDelimiter' && err.type !== 'Delimiter'
          );

          if (fatalErrors.length > 0 && (!results.data || results.data.length === 0)) {
            throw new Error(fatalErrors[0]?.message || "Unable to parse CSV file.");
          }

          const parsedHandles = extractHandlesFromCsvRows(results.data);

          if (parsedHandles.length === 0) {
            throw new Error("No valid usernames were found in the CSV.");
          }

          onCsvHandlesParsed(parsedHandles, file.name);
          toast({
            title: "CSV imported",
            description: `${parsedHandles.length} creator${parsedHandles.length === 1 ? '' : 's'} added from ${file.name}.`,
          });
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : "Invalid CSV file.";
          toast({
            title: "CSV import failed",
            description: message,
            variant: "destructive",
          });
        }
      },
      error: (error) => {
        toast({
          title: "CSV import failed",
          description: error.message || "Unable to read CSV file.",
          variant: "destructive",
        });
      },
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-700">You can add 3 more creators to your campaigns.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Select value="" onValueChange={onCreatorListSelect}>
            <SelectTrigger className="w-full border-gray-200 rounded-md h-11 text-sm text-gray-600">
              <SelectValue placeholder="Select one or multiple lists" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="favorites">Favorites</SelectItem>
              <SelectItem value="shortlisted">Shortlisted creators</SelectItem>
              <SelectItem value="previous-collaborators">Previous collaborators</SelectItem>
            </SelectContent>
          </Select>
          {selectedCreatorLists.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {selectedCreatorLists.map(listId => (
                <button
                  key={listId}
                  type="button"
                  onClick={() => onRemoveCreatorList(listId)}
                  className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                >
                  {listId.replace(/-/g, ' ')}
                  <X className="w-3 h-3 ml-2" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="creatorUsernames" className="text-sm font-medium text-gray-900 mb-2 block">
          Write or copy-paste @usernames
        </Label>
        <Textarea
          id="creatorUsernames"
          value={creatorUsernames}
          onChange={(e) => onSetCreatorUsernames(e.target.value)}
          placeholder="Add @username"
          className="w-full min-h-[200px] border-gray-200 rounded-md resize-y p-4 text-gray-600 placeholder:text-gray-400 focus:border-blue-500"
        />
      </div>

      <div className="flex items-center gap-4 py-1">
        <div className="h-px flex-1 bg-gray-200" />
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">OR</span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Import creators via CSV</h3>
            <p className="text-sm text-gray-600">
              Upload a CSV with a <span className="font-medium">username</span>, <span className="font-medium">instagram_handle</span>, or <span className="font-medium">handle</span> column.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleCsvUpload}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="h-11 px-4 text-gray-800"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import via CSV
            </Button>
          </div>
        </div>
      </div>

      {mergedHandles.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <Label className="text-sm font-medium text-gray-900">
              Creators added ({mergedHandles.length})
            </Label>
            {csvFileName && <span className="text-xs text-gray-500 truncate">Imported from {csvFileName}</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            {mergedHandles.map(handle => (
              <button
                key={handle}
                type="button"
                onClick={() => onRemoveHandle(handle)}
                className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                @{handle}
                <X className="w-3.5 h-3.5 ml-2 text-gray-500" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="pt-6 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={onSubmit}
            disabled={createLoading}
            className="bg-[#A655F8] text-white"
          >
            {createLoading ? (
              <LoadingSpinner size="small" iconClassName="text-white" label="Starting..." />
            ) : (
              "Start campaign"
            )}
          </Button>
          <Button variant="outline" onClick={onBack} disabled={createLoading}>
            Back
          </Button>
          <Button variant="outline" onClick={onClose} disabled={createLoading}>
            Cancel
          </Button>
        </div>
        {createError && <span className="text-sm text-red-500">{createError}</span>}
      </div>
    </div>
  );
};
